import {
  isEmpty,
  get,
  find,
  sample,
  shuffle,
  intersection,
  isEqual,
  cloneDeep,
  sampleSize,
} from 'lodash';
import {
  GROUP_TYPE_AB,
  GROUP_TYPE_BASIC,
  GROUP_TYPE_CONJOINT,
  GROUP_TYPE_VAN_WESTENDORP,
  GROUP_TYPE_MAXDIFF,
  GROUP_TYPE_LOOP_AND_MERGE,
} from '../../../constants';
import { shuffleArray } from '../../../utils';
import request from '../../../request';
import { loopAndMergeService, GroupNode } from '../../../logic';

export const UNSUCCESSFUL_GROUP_SETUP = 'Unsuccessful group setup';
class QuestionGroup {
  constructor({
    id,
    questions = [],
    refId,
    shuffle: shuffleType,
    attributeSet,
    type,
    displaySamePage,
  }) {
    this.id = id;
    this.refId = refId;
    this.shuffleType = shuffleType;
    this.type = type || GROUP_TYPE_BASIC;
    this.answeredQuestions = [];
    this.repetitions = 1;
    this.displaySamePage = !!displaySamePage;
    this.skip = false;

    if (type === GROUP_TYPE_CONJOINT || type === GROUP_TYPE_MAXDIFF) {
      this.block = undefined;
      const { attributes, groupings } = attributeSet;
      this.repetitions = get(attributeSet, 'properties.repetitions', 1);
      if (typeof this.repetitions === 'string') {
        this.repetitions = parseInt(this.repetitions, 10);
      }
      this.choiceSetSize = get(attributeSet, 'properties.choiceSetSize', 1);
      // excluded combinations contains arrays of attributeRefIds
      // ie: [['attribute1', 'attribute2'], ['attribute1', 'attribute3']]
      this.excludedCombinations = get(
        attributeSet,
        'properties.excludedCombinations',
        [],
      );
      if (typeof this.choiceSetSize === 'string') {
        this.choiceSetSize = parseInt(this.choiceSetSize, 10);
      }
      const richAttributes = attributes.map((attribute) => ({
        ...attribute,
        values: attribute.values.map((value) => ({
          ...value,
          attributeRefId: attribute.refId,
        })),
      }));
      this.attributes = richAttributes;
      this.attributeValues = richAttributes.reduce((accumulator, attribute) => {
        attribute.values.forEach((attributeValue) => {
          accumulator.push({
            ...attributeValue,
            attributeLabel: attribute.name,
            attributeImage: attribute.image_url,
          });
        });
        return accumulator;
      }, []);
      this.choiceSets = [];
      this.groupings = groupings || [];
    } else if (!isEmpty(attributeSet)) {
      this.selectedConcepts = [];
      this.answeredConcepts = [];
      this.type = GROUP_TYPE_AB;

      if (type === GROUP_TYPE_VAN_WESTENDORP) {
        this.type = GROUP_TYPE_VAN_WESTENDORP;
      } else if (type === GROUP_TYPE_LOOP_AND_MERGE) {
        this.type = GROUP_TYPE_LOOP_AND_MERGE;
        // loop and merge configuration
        this.loopAndMerge = get(attributeSet, 'loopAndMerge', null);
        // shuffle concepts
        this.shuffleConcepts = get(attributeSet, 'properties.shuffle', 0);
        if (typeof this.shuffleConcepts === 'string') {
          this.shuffleConcepts = parseInt(this.shuffleConcepts, 10);
        }
      }

      this.currentConcept = undefined;
      this.repetitions = get(attributeSet, 'properties.repetitions', 1);
      if (typeof this.repetitions === 'string') {
        this.repetitions = parseInt(this.repetitions, 10);
      }

      this.concepts = GroupNode.enrichConcepts(attributeSet);

      if (this.repetitions > this.concepts.length) {
        // in debug mode do not throw error, instead set repetitions equal to number of concepts
        if (Pollfish.helper.isDebugMode()) {
          this.repetitions = this.concepts.length;
        } else {
          throw new Error('More repetitions expected than given concepts');
        }
      }
    }

    this.setQuestions(questions);
  }

  setQuestions(questions) {
    const { refId, shuffleType } = this;
    // verify that all questions exist for the current group
    const notMatchingQuestions = questions.filter(
      (question) => question.groupRefId !== refId,
    );
    if (!isEmpty(notMatchingQuestions)) {
      Raven.captureMessage(
        "Tried grouping question that doesn't match the given group",
        {
          extra: {
            ...Pollfish.helper.getErrorObject(),
            notMatchingQuestions,
          },
        },
      );
    }

    this.answeredQuestions = [];
    this.questions = shuffleArray(questions, shuffleType);
  }

  addQuestion(question) {
    this.questions = [...this.questions, question];
  }

  shuffleQuestions() {
    this.questions = shuffleArray(this.questions, this.shuffleType);
  }

  isSkipped() {
    return this.skip;
  }

  getCurrentChoiceSet() {
    const id = this.getCurrentConcept();

    return find(this.choiceSets, ['id', id]);
  }

  getCurrentConcept() {
    return Pollfish.store.respondentKnowledge.getGroupCurrentConceptId(
      this.refId,
    );
  }

  getAttributesForQuestion(questionRefId) {
    const currentConcept = this.getCurrentConcept();

    if (!currentConcept) {
      const error = new Error(
        'Called getAttributesForQuestion without having a current concept',
      );
      Raven.captureException(error, {
        extras: {
          ...Pollfish.helper.getRedirectData(),
          group: {
            ...this.getData(),
          },
        },
      });
      // eslint-disable-next-line
      console.error(error);
      Pollfish.ui.noSurveyAvailable();
      return undefined;
    }

    return this.getAttributesForQuestionByConcept(
      questionRefId,
      currentConcept,
    );
  }

  getAttributesForQuestionByConcept(questionRefId, conceptRefId) {
    const selectedConcept = this.concepts.find(
      (concept) => concept.refId === conceptRefId,
    );
    const selectedQuestion = this.questions.find(
      (question) => question.refId === questionRefId,
    );
    if (!selectedConcept) {
      throw new Error('Missing concept from getAttributesForQuestionByConcept');
    }
    if (!selectedQuestion) {
      throw new Error(
        'Missing question from getAttributesForQuestionByConcept',
      );
    }

    const { selectedAttributes = [] } = selectedQuestion;
    const attributeValues = selectedAttributes.map((attributeRefId) => {
      const value = selectedConcept.attributeValues.find(
        (attributeValue) => attributeValue.attributeRefId === attributeRefId,
      );
      if (!value) {
        throw new Error('No value found for given attribute');
      }
      return value;
    });
    return attributeValues || [];
  }

  clearSelectedConcepts() {
    if (this.type === GROUP_TYPE_BASIC) {
      return;
    }
    this.selectedConcepts = [];
    this.answeredConcepts = [];
    this.currentConcept = undefined;
  }

  clearChoiceSets() {
    if (this.type !== GROUP_TYPE_CONJOINT && this.type !== GROUP_TYPE_MAXDIFF) {
      return;
    }
    this.block = undefined;
    this.choiceSets = [];
  }

  getData() {
    return {
      refId: this.refId,
      id: this.id,
      concepts: this.concepts,
      selectedConcepts: this.selectedConcepts,
      questions: this.questions,
    };
  }

  updateAdlGroupRepetitions(list) {
    Pollfish.store.respondentKnowledge.updateGroupRepetitions(
      this.refId,
      list,
      list.length,
    );
  }

  addSelectedConcepts(concepts) {
    if (isEmpty(concepts)) {
      throw new Error('Tried adding empty concepts');
    }

    concepts.forEach((concept) => this.addSelectedConcept(concept));
    this.updateAdlGroupRepetitions(concepts.map((concept) => concept.refId));
  }

  addSelectedConcept(concept) {
    if (
      this.type !== GROUP_TYPE_AB &&
      this.type !== GROUP_TYPE_VAN_WESTENDORP &&
      this.type !== GROUP_TYPE_LOOP_AND_MERGE
    ) {
      return;
    }
    this.selectedConcepts.push(concept.refId);
    if (!this.currentConcept) {
      this.currentConcept = concept.refId;
    }
  }

  addConjointBlocks(blocks) {
    // we currently only expect one block. We will need to change this in the future to support more
    this.addConjointBlock(blocks[0]);
  }

  addConjointBlock(block) {
    this.block = block;
    this.addChoiceSets(block.choiceSets);
  }

  addChoiceSets(choiceSets) {
    if (isEmpty(choiceSets)) {
      throw new Error('Tried adding empty concept sets');
    }

    choiceSets.forEach((choiceSet) => this.addChoiceSet(choiceSet));
    // TODO: achil is right to use the id?
    this.updateAdlGroupRepetitions(choiceSets.map(({ id }) => id));
  }

  addChoiceSet(choiceSet) {
    if (this.type !== GROUP_TYPE_CONJOINT && this.type !== GROUP_TYPE_MAXDIFF) {
      return;
    }
    const transformedChoiceSet = cloneDeep(choiceSet);
    transformedChoiceSet.conceptAnswers = transformedChoiceSet.conceptAnswers.map(
      (conceptAnswer) => {
        const richAttributeValues = conceptAnswer.attributeValues.map(
          (attributeValueRefId) =>
            find(this.attributeValues, ['refId', attributeValueRefId]),
        );
        return {
          ...conceptAnswer,
          attributeValues: richAttributeValues,
        };
      },
    );
    this.choiceSets.push(transformedChoiceSet);
  }

  isValid() {
    // not sure if there is a way for basic type groups to be invalid
    if (this.type === GROUP_TYPE_BASIC) {
      return true;
    }
    // group needs to have at least one selected concept for it to be valid
    if (
      this.type === GROUP_TYPE_AB ||
      this.type === GROUP_TYPE_VAN_WESTENDORP ||
      this.type === GROUP_TYPE_LOOP_AND_MERGE
    ) {
      return !isEmpty(this.selectedConcepts);
    }
    return false;
  }

  async setup() {
    const surveyId = Pollfish.survey.getSurveyId();
    const url = `/v2/surveys/${surveyId}/enter-group`;
    if (this.type === GROUP_TYPE_CONJOINT || this.type === GROUP_TYPE_MAXDIFF) {
      // NOTE: this could probably be something better
      const isAlreadySetUp = !!this.block;
      if (isAlreadySetUp) {
        return;
      }
      // we have a race condition in e2e tests running on a machine where enter-group
      // was being fulfilled before spinner show up
      await Pollfish.ui.showSpinner();
      const { blocks, success } = await request({
        url,
        data: {
          groupRefId: this.refId,
          groupType: this.type,
        },
      });
      if (!success) {
        throw new Error(UNSUCCESSFUL_GROUP_SETUP, {
          cause: {
            message: 'Server responded with success: false',
            data: {
              groupRefId: this.refId,
              groupType: this.type,
            },
          },
        });
      }
      this.addConjointBlocks(blocks);
    } else if (
      this.type === GROUP_TYPE_AB ||
      this.type === GROUP_TYPE_VAN_WESTENDORP // TODO: achil has van w concepts?
    ) {
      // NOTE: this could probably be something better
      const isAlreadySetUp = !isEmpty(this.selectedConcepts);
      if (isAlreadySetUp) {
        return;
      }
      // we have a race condition in e2e tests running on a machine where enter-group
      // was being fulfilled before spinner show up
      await Pollfish.ui.showSpinner();
      const { attributeGroupingsToShow, success } = await request({
        url,
        data: {
          groupRefId: this.refId,
          groupType: this.type,
        },
      });
      if (!success) {
        throw new Error(UNSUCCESSFUL_GROUP_SETUP, {
          cause: {
            message: 'Server responded with success: false',
            data: {
              groupRefId: this.refId,
              groupType: this.type,
            },
          },
        });
      }
      this.addSelectedConcepts(attributeGroupingsToShow);
    } else if (this.type === GROUP_TYPE_LOOP_AND_MERGE) {
      // NOTE: this could probably be something better
      const isAlreadySetUp = !isEmpty(this.selectedConcepts);
      if (isAlreadySetUp) {
        return;
      }
      // we have a race condition in e2e tests running on a machine where enter-group
      // was being fulfilled before spinner show up
      await Pollfish.ui.showSpinner();

      // get loop and merge attribute groupings
      const attributeGroupings = this.getLoopAndMergeAttributeGroupings();
      // if there are no attribute groupings, the loop and merge question group should be skipped
      if (isEmpty(attributeGroupings)) {
        return;
      }

      // if repetitions are less than attribute groupings, call server endpoint to get the
      // least shown attribute groupings or just slice them in debug mode
      if (this.repetitions < attributeGroupings.length) {
        if (Pollfish.helper.isDebugMode()) {
          const shuffledGroupings = shuffle(attributeGroupings);
          this.addSelectedConcepts(
            shuffledGroupings.slice(0, this.repetitions),
          );
        } else {
          const attributeGroupingRefIds = attributeGroupings.map(
            (ag) => ag.refId,
          );
          const { attributeGroupingsToShow, success } = await request({
            url,
            data: {
              groupRefId: this.refId,
              groupType: this.type,
              attributeGroupingRefIds,
            },
          });
          if (!success) {
            throw new Error(UNSUCCESSFUL_GROUP_SETUP, {
              cause: {
                message: 'Server responded with success: false',
                data: {
                  groupRefId: this.refId,
                  groupType: this.type,
                  attributeGroupingRefIds,
                },
              },
            });
          }
          const serverGroupingRefIds = attributeGroupingsToShow.map(
            (sgRefId) => sgRefId.refId,
          );
          const selectedGroupings = attributeGroupings.filter((grouping) =>
            serverGroupingRefIds.includes(grouping.refId),
          );

          this.addSelectedConcepts(selectedGroupings);
        }
      } else {
        // if attribute groupings are equal or less than repetitions, show them all
        this.addSelectedConcepts(attributeGroupings);
      }
    }

    // we could probably await this to finish so that the setup goes further when
    // the images have also finished but i think it's better to show content as early as possible
    this.preloadStaticAssets();
  }

  getLoopAndMergeAttributeGroupings() {
    // get loop and merge attribute groupings
    let attributeGroupings = loopAndMergeService.getLoopAndMergeAttributeGroupings(
      this.concepts,
      this.loopAndMerge,
      Pollfish.store.getRespondentKnowledge(),
      [
        ...Pollfish.screening.getQuestions(),
        ...Pollfish.survey.props.questions,
      ],
    );
    // if there are no attribute groupings, the loop and merge question group should be skipped
    if (isEmpty(attributeGroupings)) {
      this.skip = true;

      return [];
    }

    // update the original concepts attribute values because some of them may have been
    // updated for loop and merge attribute groupings (merge question with dynamic values)
    attributeGroupings.forEach((attributeGrouping) => {
      const originalConcept = find(this.concepts, [
        'refId',
        attributeGrouping.refId,
      ]);
      if (originalConcept) {
        originalConcept.attributeValues = attributeGrouping.attributeValues;
      }
    });
    // shuffle attribute groupings
    attributeGroupings = shuffleArray(attributeGroupings, this.shuffleConcepts);

    return attributeGroupings;
  }

  // eslint-disable-next-line class-methods-use-this
  preloadStaticAssetsForConcept(concept) {
    const attributeValues = get(concept, 'attributeValues', []);
    attributeValues.forEach((attributeValue) => {
      if (attributeValue.image_url) {
        const image = new Image();
        image.src = attributeValue.image_url;
      }
    });
  }

  preloadStaticAssets() {
    if (
      this.type === GROUP_TYPE_BASIC ||
      this.type === GROUP_TYPE_LOOP_AND_MERGE
    ) {
      return;
    }
    if (this.type === GROUP_TYPE_AB) {
      this.concepts.forEach((concept) => {
        if (this.selectedConcepts.includes(concept.refId)) {
          this.preloadStaticAssetsForConcept(concept);
        }
      });
    } else if (this.type === GROUP_TYPE_CONJOINT) {
      this.choiceSets.forEach((choiceSet) => {
        choiceSet.conceptAnswers.forEach(this.preloadStaticAssetsForConcept);
      });
    } else if (this.type === GROUP_TYPE_VAN_WESTENDORP) {
      this.concepts.forEach(this.preloadStaticAssetsForConcept);
    } else {
      // eslint-disable-next-line
      console.error(`Missing preloading handling for group type ${this.type}`);
    }
  }

  randomizeConcepts() {
    if (
      this.type === GROUP_TYPE_AB ||
      this.type === GROUP_TYPE_VAN_WESTENDORP
    ) {
      this.randomizeConceptsForAB();
    } else if (
      this.type === GROUP_TYPE_CONJOINT ||
      this.type === GROUP_TYPE_MAXDIFF
    ) {
      this.randomizeConceptsForConjointAndMaxDiff();
    }
  }

  randomizeConceptsForAB() {
    this.clearSelectedConcepts();
    const shuffledConcepts = shuffle(this.concepts);
    this.addSelectedConcepts(shuffledConcepts.slice(0, this.repetitions));
    this.preloadStaticAssets();
  }

  randomizeConceptsForConjointAndMaxDiff() {
    this.clearChoiceSets();
    this.block = {
      // just a random number since this is the preview
      id: 5,
    };

    if (isEmpty(this.groupings)) {
      this.randomizeConceptsWithoutGroupings();
    } else {
      this.randomizeConceptsWithGroupings();
    }
  }

  randomizeConceptsWithoutGroupings() {
    let conceptIndex = 0;
    // we need to be careful not to show the same combination twice
    const combinationIds = [];
    let combinationTries = 0;
    while (this.choiceSets.length < this.repetitions) {
      const choiceSet = {
        id: combinationTries,
        conceptAnswers: [],
      };
      while (choiceSet.conceptAnswers.length < this.choiceSetSize) {
        const conceptAnswer = {
          index: (conceptIndex += 1),
          id: (conceptIndex += 1),
          body: `${(conceptIndex += 1)}`,
          attributeValues: [],
        };
        const ids = [];
        this.attributes.forEach((attribute) => {
          if (isEmpty(attribute.values)) {
            return;
          }
          const randomAttributeValue = sample(attribute.values);
          conceptAnswer.attributeValues.push(randomAttributeValue.refId);
          ids.push(randomAttributeValue.refId);
        });
        const combinationId = ids.join(':');
        const tooManyTries = combinationTries > this.attributes.length ** 2;
        const wayTooManyTries = combinationTries > this.attributes.length ** 10;
        // eslint-disable-next-line no-loop-func
        const shouldExcludeCombination = this.excludedCombinations.some(
          (combination) => {
            if (isEmpty(combination)) {
              return false;
            }
            // lets say we want to always exclude this combination: ['engine-level-0', 'size-level-0']
            // then the check needs to eliminate all combinations CONTAINING the above one
            //  By performing an intersection between the two arrays (the combination we
            // created and the excluded ones) we get a resulting array with all the values matching
            // both inputs. If the intersected array contains ALL of the elements of the excluded one,
            // then we should eliminate the resulting combination
            return isEqual(intersection(ids, combination), combination);
          },
        );
        if (shouldExcludeCombination && wayTooManyTries) {
          // we shouldn't allow the ui to go into an endless loop
          break;
        } else if (
          !shouldExcludeCombination &&
          (!combinationIds.includes(combinationId) || tooManyTries)
        ) {
          choiceSet.conceptAnswers.push(conceptAnswer);
        }
        combinationTries += 1;
      }
      this.addChoiceSet(choiceSet);
    }

    this.updateAdlGroupRepetitions(this.choiceSets.map(({ id }) => id));
  }

  randomizeConceptsWithGroupings() {
    let conceptIndex = 0;
    for (let index = 0; index < this.repetitions; index += 1) {
      const choiceSet = {
        id: index,
        conceptAnswers: [],
      };
      const randomGroupings = sampleSize(this.groupings, this.choiceSetSize);
      // eslint-disable-next-line no-loop-func
      randomGroupings.forEach((randomGrouping) => {
        const conceptAnswer = {
          index: (conceptIndex += 1),
          id: randomGrouping.id,
          body: `${(conceptIndex += 1)}`,
          attributeValues: randomGrouping.attributeValues,
        };

        conceptAnswer.attributeValues.map((attributeValueRefId) =>
          find(this.attributeValues, ['refId', attributeValueRefId]),
        );
        choiceSet.conceptAnswers.push(conceptAnswer);
      });
      this.addChoiceSet(choiceSet);
    }

    this.updateAdlGroupRepetitions(this.choiceSets.map(({ id }) => id));
  }

  isDisplaySamePage() {
    return this.displaySamePage;
  }
}

export default QuestionGroup;
